import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Wallet as WalletIcon, Trophy, ArrowRight, Users } from 'lucide-react';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Spinner } from '../components/common/Spinner';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../hooks/useWallet';
import { useMyTickets } from '../hooks/useTickets';
import { formatCurrency } from '../utils/formatters';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data: walletData, isLoading: walletLoading } = useWallet();
  const { data: ticketsData, isLoading: ticketsLoading } = useMyTickets({ limit: 10 });
  const { data: winningTicketsData, isLoading: winsLoading } = useMyTickets({ limit: 5, status: 'WON' });

  const wallet = walletData?.data;
  const tickets = ticketsData?.data?.tickets || [];
  const winningTickets = winningTicketsData?.data?.tickets || [];

  const isLoading = walletLoading || ticketsLoading || winsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome back, {user?.firstName}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Here's what's happening with your account</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link to="/wallet">
            <Card className="relative overflow-hidden h-full cursor-pointer group">
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-50 dark:bg-indigo-950 rounded-full -mr-6 -mt-6" />
              <div className="relative">
                <WalletIcon className="h-5 w-5 text-indigo-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Balance</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {wallet ? formatCurrency(wallet.balance, wallet.currency) : '—'}
                </p>
                <p className="text-sm font-medium text-indigo-600 mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Manage wallet <ArrowRight className="h-3.5 w-3.5" />
                </p>
              </div>
            </Card>
          </Link>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-50 dark:bg-purple-950 rounded-full -mr-6 -mt-6" />
            <div className="relative">
              <Ticket className="h-5 w-5 text-purple-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{ticketsData?.data?.pagination?.total || 0}</p>
            </div>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-50 dark:bg-amber-950 rounded-full -mr-6 -mt-6" />
            <div className="relative">
              <Trophy className="h-5 w-5 text-amber-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Wins</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{winningTicketsData?.data?.pagination?.total || 0}</p>
            </div>
          </Card>

          <Link to="/affiliate">
            <Card className="relative overflow-hidden h-full cursor-pointer group">
              <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-50 dark:bg-emerald-950 rounded-full -mr-6 -mt-6" />
              <div className="relative">
                <Users className="h-5 w-5 text-emerald-600 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Affiliate</p>
                <p className="text-sm font-medium text-indigo-600 mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Earn commissions <ArrowRight className="h-3.5 w-3.5" />
                </p>
              </div>
            </Card>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Tickets</h2>
              <Link to="/my-tickets" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <Card>
              {tickets.length === 0 ? (
                <div className="text-center py-10">
                  <Ticket className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No tickets yet</p>
                  <Link to="/raffles">
                    <Button variant="outline" size="sm" className="mt-3">Browse Raffles</Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {tickets.map((ticket) => (
                    <div key={ticket.id} className="py-3 first:pt-0 last:pb-0">
                      <Link to={`/raffles/${ticket.raffleId}`} className="group">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 transition-colors text-sm">{ticket.raffle?.name}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">#{ticket.ticketNumber}</p>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(ticket.purchasePrice, ticket.raffle?.currency || 'USD')}</span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" /> Recent Wins
              </h2>
            </div>
            <Card>
              {winningTickets.length === 0 ? (
                <div className="text-center py-10">
                  <Trophy className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No wins yet — keep playing</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {winningTickets.map((ticket) => (
                    <div key={ticket.id} className="py-3 first:pt-0 last:pb-0">
                      <Link to={`/raffles/${ticket.raffleId}`} className="group">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 transition-colors text-sm">{ticket.raffle?.name}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {ticket.drawDate ? new Date(ticket.drawDate).toLocaleDateString() : 'Pending'}
                            </p>
                          </div>
                          <span className="text-sm font-semibold text-emerald-600">
                            +{ticket.prizeAmount ? formatCurrency(ticket.prizeAmount, ticket.raffle?.currency || 'USD') : 'Pending'}
                          </span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
};
